import React, { useState } from 'react';
import { SafeRender } from '../../../utils/formattingUtils';
import { getFirestore, doc, updateDoc, deleteField } from 'firebase/firestore';
import { TrashIcon } from '@heroicons/react/24/outline';

// Helper function for safely rendering preference items
const renderPreferenceItem = (item) => {
  if (typeof item === 'string') {
    return item;
  } else if (typeof item === 'object') {
    // Handle objects with text and id properties
    return item.text || item.name || JSON.stringify(item);
  } else {
    return String(item);
  }
};

export default function CandidateProfileTab({ user }) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  if (!user) return null;

  const handleDeleteProfileSummary = async () => {
    if (!user.id) return;
    if (!window.confirm('Are you sure you want to delete this profile summary? This action cannot be undone.')) return;

    setIsDeleting(true);
    setDeleteError('');
    
    try {
      const db = getFirestore();
      
      // Update the user_profile_summaries collection to remove the profile summary
      const summaryRef = doc(db, "user_profile_summaries", user.id);
      await updateDoc(summaryRef, {
        profileSummary: deleteField(),
        lastUpdated: new Date(),
        wasEditedByUser: false
      });
      
      setDeleteSuccess(true);
      setTimeout(() => {
        setDeleteSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Error deleting profile summary:", error);
      setDeleteError('Failed to delete profile summary. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Summary - Moved to top */}
      {user.profileSummary && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">Profile Summary</h3>
              <div className="flex items-center">
                {deleteSuccess && (
                  <span className="text-sm text-green-600 dark:text-green-400 mr-3">
                    Summary deleted successfully!
                  </span>
                )}
                {deleteError && (
                  <span className="text-sm text-red-600 dark:text-red-400 mr-3">
                    {deleteError}
                  </span>
                )}
                <button
                  onClick={handleDeleteProfileSummary}
                  disabled={isDeleting}
                  className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded
                    ${isDeleting 
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500' 
                      : 'bg-red-100 text-red-700 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:bg-red-900 dark:text-red-300 dark:hover:bg-red-800'}`}
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  {isDeleting ? 'Deleting...' : 'Delete Summary'}
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">{user.profileSummary}</p>

            {/* Key skills */}
            {user.keySkills && user.keySkills.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Key Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {user.keySkills.map((skill, index) => (
                    <span key={index} className="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300 ring-1 ring-inset ring-green-600/20 dark:ring-green-500/30">
                      {typeof skill === 'string' 
                        ? skill 
                        : (typeof skill === 'object' 
                            ? (skill.name || skill.text || JSON.stringify(skill)) 
                            : String(skill))}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Pain Points */}
            {user.painPoints && user.painPoints.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Pain Points</h4>
                <div className="flex flex-wrap gap-2">
                  {user.painPoints.map((point, index) => (
                    <span key={index} className="inline-flex items-center rounded-md bg-red-50 dark:bg-red-900 px-2 py-1 text-xs font-medium text-red-700 dark:text-red-300 ring-1 ring-inset ring-red-600/20 dark:ring-red-500/30">
                      {point}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Basic User Information */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">Basic Information</h3>
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-6">
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                {user.displayName === null ? 'Unknown User' : 
                  (typeof user.displayName === 'object' ? user.displayName.text : user.displayName)}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">{user.email}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Registration Date</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200"><SafeRender value={user.createdAt} /></dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Login</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200"><SafeRender value={user.lastLogin} /></dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">User ID</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200 break-all">{user.id}</dd>
            </div>
            {user.submissionId && (
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Waitlist Submission ID</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200 break-all">{user.submissionId}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Profile Information */}
      {(user.hasUserProfile || user.basicInfo) && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">Profile Details</h3>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6">
              {(user.basicInfo?.firstName || user.firstName) && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">First Name</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                    {user.basicInfo?.firstName || user.firstName}
                  </dd>
                </div>
              )}
              {(user.basicInfo?.lastName || user.lastName) && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Name</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                    {user.basicInfo?.lastName || user.lastName}
                  </dd>
                </div>
              )}
              {(user.basicInfo?.linkedinUrl || user.linkedinUrl) && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">LinkedIn URL</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                    <a 
                      href={user.basicInfo?.linkedinUrl || user.linkedinUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 break-all"
                    >
                      {user.basicInfo?.linkedinUrl || user.linkedinUrl}
                    </a>
                  </dd>
                </div>
              )}
            </dl>

            {/* Waitlist Data / Quiz Answers */}
            {((user.preferences && Object.keys(user.preferences).length > 0) || 
              (user.waitlistData?.quizAnswers && Object.keys(user.waitlistData.quizAnswers).length > 0)) && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Preferences</h4>
                <div className="bg-white dark:bg-gray-700 p-4 rounded border border-gray-200 dark:border-gray-600 text-sm">
                  {user.preferences && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Culture */}
                      {user.preferences.culture && (
                        <div>
                          <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Culture</h5>
                          <div className="flex flex-wrap gap-1">
                            {Array.isArray(user.preferences.culture) ? (
                              user.preferences.culture.map((item, idx) => (
                                <span key={idx} className="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-900 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 ring-1 ring-inset ring-blue-600/20 dark:ring-blue-500/30">
                                  {renderPreferenceItem(item)}
                                </span>
                              ))
                            ) : (
                              <span className="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-900 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 ring-1 ring-inset ring-blue-600/20 dark:ring-blue-500/30">
                                {renderPreferenceItem(user.preferences.culture)}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Environment */}
                      {user.preferences.environment && (
                        <div>
                          <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Environment</h5>
                          <div className="flex flex-wrap gap-1">
                            {Array.isArray(user.preferences.environment) ? (
                              user.preferences.environment.map((item, idx) => (
                                <span key={idx} className="inline-flex items-center rounded-md bg-purple-50 dark:bg-purple-900 px-2 py-1 text-xs font-medium text-purple-700 dark:text-purple-300 ring-1 ring-inset ring-purple-600/20 dark:ring-purple-500/30">
                                  {renderPreferenceItem(item)}
                                </span>
                              ))
                            ) : (
                              <span className="inline-flex items-center rounded-md bg-purple-50 dark:bg-purple-900 px-2 py-1 text-xs font-medium text-purple-700 dark:text-purple-300 ring-1 ring-inset ring-purple-600/20 dark:ring-purple-500/30">
                                {renderPreferenceItem(user.preferences.environment)}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Ideal Role */}
                      {user.preferences.idealRole && (
                        <div>
                          <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Ideal Role</h5>
                          <div className="flex flex-wrap gap-1">
                            {Array.isArray(user.preferences.idealRole) ? (
                              user.preferences.idealRole.map((item, idx) => (
                                <span key={idx} className="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300 ring-1 ring-inset ring-green-600/20 dark:ring-green-500/30">
                                  {renderPreferenceItem(item)}
                                </span>
                              ))
                            ) : (
                              <span className="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300 ring-1 ring-inset ring-green-600/20 dark:ring-green-500/30">
                                {renderPreferenceItem(user.preferences.idealRole)}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Industry */}
                      {user.preferences.industry && (
                        <div>
                          <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Industry</h5>
                          <div className="flex flex-wrap gap-1">
                            {Array.isArray(user.preferences.industry) ? (
                              user.preferences.industry.map((item, idx) => (
                                <span key={idx} className="inline-flex items-center rounded-md bg-yellow-50 dark:bg-yellow-900 px-2 py-1 text-xs font-medium text-yellow-700 dark:text-yellow-300 ring-1 ring-inset ring-yellow-600/20 dark:ring-yellow-500/30">
                                  {renderPreferenceItem(item)}
                                </span>
                              ))
                            ) : (
                              <span className="inline-flex items-center rounded-md bg-yellow-50 dark:bg-yellow-900 px-2 py-1 text-xs font-medium text-yellow-700 dark:text-yellow-300 ring-1 ring-inset ring-yellow-600/20 dark:ring-yellow-500/30">
                                {renderPreferenceItem(user.preferences.industry)}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Job Aspects */}
                      {user.preferences.jobAspects && (
                        <div>
                          <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Job Aspects</h5>
                          <div className="flex flex-wrap gap-1">
                            {Array.isArray(user.preferences.jobAspects) ? (
                              user.preferences.jobAspects.map((item, idx) => (
                                <span key={idx} className="inline-flex items-center rounded-md bg-indigo-50 dark:bg-indigo-900 px-2 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 ring-1 ring-inset ring-indigo-600/20 dark:ring-indigo-500/30">
                                  {renderPreferenceItem(item)}
                                </span>
                              ))
                            ) : (
                              <span className="inline-flex items-center rounded-md bg-indigo-50 dark:bg-indigo-900 px-2 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 ring-1 ring-inset ring-indigo-600/20 dark:ring-indigo-500/30">
                                {renderPreferenceItem(user.preferences.jobAspects)}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Passions */}
                      {user.preferences.passions && (
                        <div>
                          <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Passions</h5>
                          <div className="flex flex-wrap gap-1">
                            {Array.isArray(user.preferences.passions) ? (
                              user.preferences.passions.map((item, idx) => (
                                <span key={idx} className="inline-flex items-center rounded-md bg-pink-50 dark:bg-pink-900 px-2 py-1 text-xs font-medium text-pink-700 dark:text-pink-300 ring-1 ring-inset ring-pink-600/20 dark:ring-pink-500/30">
                                  {renderPreferenceItem(item)}
                                </span>
                              ))
                            ) : (
                              <span className="inline-flex items-center rounded-md bg-pink-50 dark:bg-pink-900 px-2 py-1 text-xs font-medium text-pink-700 dark:text-pink-300 ring-1 ring-inset ring-pink-600/20 dark:ring-pink-500/30">
                                {renderPreferenceItem(user.preferences.passions)}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Stage */}
                      {user.preferences.stage && (
                        <div>
                          <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Stage</h5>
                          <span className="inline-flex items-center rounded-md bg-gray-50 dark:bg-gray-900 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-600/20 dark:ring-gray-500/30">
                            {user.preferences.stage}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Fallback for legacy data */}
                  {!user.preferences && user.waitlistData?.quizAnswers && (
                    <div className="bg-white dark:bg-gray-700 p-3 rounded border border-gray-200 dark:border-gray-600 text-sm max-h-60 overflow-y-auto">
                      <pre className="whitespace-pre-wrap text-xs text-gray-800 dark:text-gray-200">
                        {JSON.stringify(user.waitlistData.quizAnswers, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Resume */}
            {(user.resumeUrl || user.resume?.resumeUrl) && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Resume</h4>
                <a 
                  href={user.resumeUrl || user.resume?.resumeUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                >
                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  {user.resumeFilename || user.resume?.filename || 'View Resume'}
                </a>
              </div>
            )}

            {/* Video Submissions */}
            {(user.videoSubmission?.videoUrl || user.videoResponses) && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Video Submission</h4>
                {user.videoSubmission?.videoUrl && (
                  <a 
                    href={user.videoSubmission.videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    View Video Submission
                  </a>
                )}
                {user.videoResponses && Object.entries(user.videoResponses).length > 0 && (
                  <div className="mt-2 space-y-2">
                    {Object.entries(user.videoResponses).map(([id, response]) => (
                      <div key={id} className="pl-2 border-l-2 border-indigo-200 dark:border-indigo-700">
                        <p className="text-xs font-medium text-gray-800 dark:text-gray-300 mb-1">
                          {response.question || id}
                        </p>
                        {response.videoUrl && (
                          <a 
                            href={response.videoUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 flex items-center text-xs"
                          >
                            <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Watch Video
                          </a>
                        )}
                        {response.transcript && (
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            <p className="font-medium">Transcript:</p>
                            <p className="italic text-gray-500 dark:text-gray-400 text-xs line-clamp-2">
                              {response.transcript.substring(0, 100)}...
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Waitlist Data (if not already shown) */}
      {user.waitlistData && !user.preferences && !user.profileSummary && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">Waitlist Information</h3>

            {user.waitlistData.submittedAt && (
              <dl className="grid grid-cols-1 gap-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Submitted At</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                    <SafeRender value={user.waitlistData.submittedAt} />
                  </dd>
                </div>
              </dl>
            )}

            {user.waitlistData.quizAnswers && Object.keys(user.waitlistData.quizAnswers).length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Quiz Answers</h4>
                <div className="bg-white dark:bg-gray-700 p-3 rounded border border-gray-200 dark:border-gray-600 text-sm max-h-60 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-xs text-gray-800 dark:text-gray-200">{JSON.stringify(user.waitlistData.quizAnswers, null, 2)}</pre>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
} 